import type { AuthorizationResponse } from './authorization-response';

/**
 * The channel between the hidden silent-renew iframe and the window that owns the
 * session.
 *
 * The iframe does not exchange the code itself, for two reasons. The refresh token is a
 * module-level variable, and a module in the iframe is a different realm — a token
 * obtained there would be stranded and lost when the frame navigates again. And in
 * development the iframe has no configuration at all: `OIDC_BASE_URL` and friends only
 * appear once the bootstrap response is merged onto `window`, which happens after this
 * code runs. Forwarding the raw response upward keeps the bridge configuration-free.
 */

/**
 * Browsing-context name of the silent-renew frame, and the discriminator the bridge
 * keys off.
 *
 * `window.name` rather than the frame element's `id`: the legacy implicit renewal uses
 * `id="refresh"` on its own iframe, so keying off the id would short-circuit the
 * implicit flow too and break it — nothing would be left to store its token. The name is
 * set only by the code-flow renewal, survives the cross-origin trip to the IdP and back,
 * needs no access to the parent, and cannot be set by an embedder.
 */
export const SILENT_RENEW_FRAME_NAME = 'oidc-silent-renew';

export const SILENT_RENEW_MESSAGE = 'oidc:silent-code';

/** Default deadline for a silent renewal, matching the implicit flow's poll timeout. */
export const SILENT_RENEW_TIMEOUT_MS = 15_000;

interface SilentRenewMessage {
  type: typeof SILENT_RENEW_MESSAGE;
  state?: string;
  code?: string;
  error?: string;
  errorDescription?: string;
}

/** True when this document is the silent-renew frame and must not boot the app. */
export function isSilentRenewFrame(): boolean {
  try {
    return window.parent !== window && window.name === SILENT_RENEW_FRAME_NAME;
  } catch {
    return false;
  }
}

/**
 * Forwards whatever the IdP left on the URL to the parent window.
 *
 * Sends unconditionally — including when there is neither a code nor an error. The
 * frame's first `about:blank` load, a redirect chain inside the IdP and an IdP error page
 * all land here with nothing useful, and staying silent in those cases would let the
 * whole app boot inside a hidden iframe: a second heartbeat, a second idle ticker, a
 * second response interceptor on the shared http client, and every remote widget fetched
 * again. The parent ignores messages that do not match its request.
 */
export function postCodeToParent(): void {
  const params = new URLSearchParams(window.location.search);

  const message: SilentRenewMessage = {
    type: SILENT_RENEW_MESSAGE,
    state: params.get('state') ?? undefined,
    code: params.get('code') ?? undefined,
    error: params.get('error') ?? undefined,
    errorDescription: params.get('error_description') ?? undefined
  };

  try {
    window.parent.postMessage(message, window.location.origin);
  } catch (error) {
    // Parent detached, or no longer same-origin. Nothing to do: the parent's deadline
    // will conclude the renewal failed.
    console.warn('[Token Worker] Failed to forward the silent renew response:', error);
  }
}

/**
 * Waits for the frame to report the authorization response for one specific request.
 *
 * Acceptance needs all four checks. The origin and `event.source` checks reject anything
 * that is not this frame, and the `state` check is what separates this renewal's answer
 * from a previous one's: `contentWindow` is a live reference that follows the frame
 * through its navigations, so it cannot by itself tell two successive renewals apart. A
 * message whose state does not match — including an IdP error that echoed no state — is
 * dropped, and the deadline concludes the renewal instead.
 */
export function waitForSilentRenewCode({
  state,
  frame,
  timeoutMs = SILENT_RENEW_TIMEOUT_MS
}: {
  state: string;
  frame: HTMLIFrameElement;
  timeoutMs?: number;
}): Promise<AuthorizationResponse> {
  return new Promise<AuthorizationResponse>((resolve, reject) => {
    const settle = (finish: () => void) => {
      window.removeEventListener('message', onMessage);
      clearTimeout(timer);
      finish();
    };

    const onMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (event.source !== frame.contentWindow) return;

      const data = event.data as SilentRenewMessage | undefined;
      if (data?.type !== SILENT_RENEW_MESSAGE) return;
      if (data.state !== state) return;

      settle(() =>
        resolve({
          code: data.code,
          state: data.state,
          error: data.error,
          errorDescription: data.errorDescription
        })
      );
    };

    const timer = setTimeout(
      () => settle(() => reject(new Error('Silent renew timed out'))),
      timeoutMs
    );

    window.addEventListener('message', onMessage);
  });
}
